import React, { useContext, useState } from 'react';
import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Toaster, toast } from 'sonner';
import {
  GraduationCap,
  User,
  Building2,
  ChevronRight,
  ArrowLeft,
} from 'lucide-react';

import { ROLES } from '../../../App';
import { AuthenticationContext } from '../../../context/Authentication';
import useAuth from '../../../hooks/useAuth';
import { getRedirectPath } from '../../../util/AuthRedirect';

export default function RegistrationOptions(props) {
  const navigate = useNavigate();
  const authentication = useContext(AuthenticationContext);
  const { setAuth } = useAuth();
  const [showloginButton, setShowloginButton] = useState(true);
  const server_url = process.env.REACT_APP_API_URI;
  const googleClientID = process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID;
  const location = useLocation();

  // Google Login check
  const checkLogin = async (user) => {
    try {
      const response = await axios.post(
        `${server_url}/user/google-login`,
        JSON.stringify({
          name: user.name,
          email: user.email,
          token: user.token,
        }),
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );
      if (response.data.isLoggedIn === true) {
        const data = jwtDecode(response.data.token);
        const { token } = response.data;
        const roles = data.role;
        const { email } = data;
        setAuth({ email, roles, token });
        authentication.login();
        localStorage.setItem('loginToken', response.data.token);
        toast.success('Registration/Login successful!');
        navigate(getRedirectPath({ location, roles, adminRole: ROLES.Admin }));
      } else {
        toast.error(response.data.msg || 'Google registration failed');
      }
    } catch (err) {
      console.error(err);
      toast.error('An error occurred during Google registration.');
    }
  };

  const optionCards = [
    {
      id: 2,
      title: 'Student / Faculty',
      description:
        'Rate textbooks, classes, schools, and movies. Faculty can review educational resources and articles.',
      icon: GraduationCap,
      color: 'text-emerald-400',
      bgHover: 'hover:border-emerald-500/50 hover:bg-emerald-500/10',
    },
    {
      id: 3,
      title: 'Consumer',
      description:
        'Explore and review entertainment categories, movies, and share your thoughts with the community.',
      icon: User,
      color: 'text-blue-400',
      bgHover: 'hover:border-blue-500/50 hover:bg-blue-500/10',
    },
    {
      id: 4,
      title: 'Company',
      description:
        'List your products, gather reviews from genuine users, and offer rewards to loyal customers.',
      icon: Building2,
      color: 'text-orange-400',
      bgHover: 'hover:border-orange-500/50 hover:bg-orange-500/10',
    },
  ];

  return (
    <>
      <Toaster theme="dark" position="top-right" richColors />
      <div className="flex flex-col items-center justify-center min-h-[calc(100vh-100px)] py-12 bg-primary font-montserrat px-4 relative">
        <button
          className="absolute left-6 top-6 sm:left-12 sm:top-12 flex items-center text-sm gap-2 text-slate-400 hover:text-white transition-colors cursor-pointer"
          onClick={() => navigate('/')}
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </button>

        <div className="w-full max-w-4xl flex flex-col lg:flex-row gap-8 lg:gap-16 items-center lg:items-start justify-between">
          {/* Left Column - Heading */}
          <div className="w-full lg:w-5/12 text-center lg:text-left pt-0 lg:pt-16">
            <h1 className="text-4xl lg:text-5xl font-extrabold text-white tracking-tight mb-4">
              Join{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-emerald-400">
                KnowQuest
              </span>
            </h1>
            <p className="text-slate-400 text-lg mb-8 max-w-md mx-auto lg:mx-0 leading-relaxed">
              Create an account to start reviewing, rating, and sharing your
              experiences across various categories.
            </p>

            <div className="hidden lg:block space-y-6">
              <div className="pb-6 border-b border-slate-800">
                <GoogleOAuthProvider clientId={googleClientID}>
                  {showloginButton && (
                    <GoogleLogin
                      theme="outline"
                      size="large"
                      shape="pill"
                      width="300"
                      text="continue_with"
                      onSuccess={(credentialResponse) => {
                        const user = jwtDecode(credentialResponse.credential);
                        setShowloginButton(false);
                        checkLogin(user);
                      }}
                      onError={() => {
                        console.error('Login Failed');
                        toast.error('Google Sign-In failed');
                      }}
                    />
                  )}
                </GoogleOAuthProvider>
              </div>

              <div className="text-slate-400 text-sm flex flex-col gap-2">
                <p>
                  Already have an account?{' '}
                  <Link
                    to="/login"
                    className="text-blue-400 hover:text-blue-300 font-semibold transition-colors"
                  >
                    Log in
                  </Link>
                </p>
                <p>
                  Business account?{' '}
                  <Link
                    to="/businesslogin"
                    className="text-orange-400 hover:text-orange-300 font-semibold transition-colors"
                  >
                    Log in here
                  </Link>
                </p>
              </div>
            </div>
          </div>

          {/* Right Column - Cards */}
          <div className="w-full lg:w-7/12 flex flex-col gap-4">
            {optionCards.map((card, index) => {
              const Icon = card.icon;
              return (
                <button
                  key={card.id}
                  onClick={() => props.registerBtn(card.id)}
                  className={`group p-6 rounded-2xl bg-slate-800/40 border border-slate-700/50 flex items-center gap-6 text-left transition-all duration-300 cursor-pointer animate-in slide-in-from-right-8 fade-in shadow-lg ${card.bgHover}`}
                  style={{ animationDelay: `${index * 150}ms` }}
                >
                  <div
                    className={`w-14 h-14 rounded-full bg-slate-900/50 border border-slate-700 flex items-center justify-center shrink-0 ${card.color} group-hover:scale-110 transition-transform duration-300`}
                  >
                    <Icon strokeWidth={1.5} className="w-7 h-7" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white mb-1 group-hover:text-blue-100 transition-colors">
                      {card.title}
                    </h3>
                    <p className="text-sm text-slate-400 leading-relaxed max-w-md group-hover:text-slate-300 transition-colors">
                      {card.description}
                    </p>
                  </div>
                  <ChevronRight className="w-6 h-6 text-slate-600 group-hover:text-white group-hover:translate-x-1 transition-all" />
                </button>
              );
            })}

            {/* Mobile Footer Links (only visible on sm/md screens) */}
            <div className="lg:hidden mt-8 flex flex-col items-center gap-6">
              <GoogleOAuthProvider clientId={googleClientID}>
                {showloginButton && (
                  <GoogleLogin
                    theme="outline"
                    size="large"
                    shape="pill"
                    text="continue_with"
                    onSuccess={(credentialResponse) => {
                      const user = jwtDecode(credentialResponse.credential);
                      setShowloginButton(false);
                      checkLogin(user);
                    }}
                    onError={() => {
                      console.log('Login Failed');
                      toast.error('Google Sign-In failed');
                    }}
                  />
                )}
              </GoogleOAuthProvider>
              <div className="text-slate-400 text-sm text-center flex flex-col gap-2">
                <p>
                  Already have an account?{' '}
                  <Link
                    to="/login"
                    className="text-blue-400 hover:text-blue-300 font-semibold"
                  >
                    Log in
                  </Link>
                </p>
                <p>
                  Business account?{' '}
                  <Link
                    to="/businesslogin"
                    className="text-orange-400 hover:text-orange-300 font-semibold"
                  >
                    Log in here
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
